import { useQuery } from "@tanstack/react-query";
import { ExternalLink, SearchX } from "lucide-react";
import { Link, Navigate, useParams } from "react-router";
import { api } from "../api";
import { LaneBadge } from "../components/lane-badge";
import { MergeTicket } from "../components/merge-ticket";
import { EmptyState, PageSkeleton, SectionHeader } from "../components/page";
import { PropertyList, PropertyRow } from "../components/property-list";
import { StatusBadge } from "../components/status-badge";
import { AddLoopsDialog, LoopBudgetRow } from "../components/tasks/loop-budget";
import {
  ArtifactsCard,
  isOpenWorkOrder,
  OperatorInstructions,
  PullFromLineButton,
  type TaskDetail,
  useProduction,
  useTaskDetail,
  WorkOrderMeta,
  WorkOrderProduction,
} from "../components/tasks/work-order";
import { Badge } from "../components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useResumeTask } from "../hooks/use-actions";
import { useIsAdmin } from "../lib/permissions";
import { holdReason, issueKey } from "../lib/tasks";
import { useFactory } from "../store";

/**
 * A work order on its own page. One linked to a Skynet issue lives on that issue's Factory tab, so
 * this page redirects there and only renders manual orders, detached specialist runs, orders from
 * an outside tracker, and any order its issue does not point back to.
 */
export function TaskDetailPage() {
  const { id = "" } = useParams();
  const query = useTaskDetail(id);
  const task = query.data?.task;
  const linkedKey = task?.trackerProvider === "skynet" ? issueKey(task) : undefined;
  const issueQuery = useQuery({
    queryKey: ["issue", linkedKey],
    queryFn: () => api.issue(linkedKey!),
    enabled: Boolean(linkedKey),
    retry: false,
  });

  if (query.isLoading || (linkedKey && issueQuery.isLoading)) return <PageSkeleton />;
  if (!query.data) {
    return (
      <EmptyState
        icon={SearchX}
        title="Work order not found"
        description="It may have been removed or the link is out of date."
      >
        <Button variant="outline" asChild>
          <Link to="/tasks">Back to work orders</Link>
        </Button>
      </EmptyState>
    );
  }
  if (linkedKey && issueQuery.data?.issue.taskId === id) {
    return <Navigate to={`/issues/${linkedKey}?view=factory`} replace />;
  }
  return <StandaloneWorkOrder data={query.data} />;
}

function StandaloneWorkOrder({ data }: { data: TaskDetail }) {
  const { agentById } = useFactory();
  const admin = useIsAdmin();
  const { task, artifacts, mergeQueue, loopBudget } = data;
  const { spend, detached } = useProduction(data);
  const resumeTask = useResumeTask(task.id);
  const hold = holdReason(task);
  const issue = task.issueSnapshot;
  const key = issueKey(task);

  return (
    <>
      <Breadcrumb className="text-xs">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/tasks">Work orders</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-mono text-xs">{key ?? "Manual order"}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="-mt-3 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={task.status} />
            <LaneBadge task={task} />
          </div>
          <h1 className="text-2xl leading-tight font-semibold tracking-[-0.022em] text-balance">
            {task.title}
          </h1>
          <WorkOrderMeta task={task} spend={spend} />
        </div>
        <div className="flex flex-wrap gap-2">
          {task.issueUrl && (
            <Button variant="outline" asChild>
              <a href={task.issueUrl} target="_blank" rel="noreferrer">
                <ExternalLink /> Open {key ?? "issue"}
              </a>
            </Button>
          )}
          {isOpenWorkOrder(task) && <PullFromLineButton taskId={task.id} />}
        </div>
      </div>

      {task.status === "waiting_for_human" && (
        <Card className="border-violet-500/30 bg-violet-500/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="space-y-1">
              <p className="text-sm font-medium text-violet-700 dark:text-violet-300">
                {hold?.kind === "loop_budget"
                  ? `Loop budget spent — ${hold.used} of ${hold.allowed} Fixer loops used`
                  : hold?.kind === "no_progress"
                    ? "The Fixer pushed nothing — waiting on a person"
                    : `${agentById.get(task.currentAgentId ?? "")?.name ?? "The reviewer"} requested changes — waiting on a person`}
              </p>
              <p className="text-xs text-muted-foreground">
                {hold?.kind === "loop_budget" ? (
                  admin ? (
                    "The Verifier still wants changes. Add loops to send its findings back to the Fixer, or continue to business testing."
                  ) : (
                    "The Verifier still wants changes. Ask an admin to add loops."
                  )
                ) : hold?.kind === "no_progress" ? (
                  <>
                    The pull request head is still at{" "}
                    <span className="font-mono text-foreground">{hold.headSha.slice(0, 7)}</span>. Request
                    rework, or continue to business testing.
                  </>
                ) : (
                  "Request rework (Fixer → review again) or continue to business testing."
                )}
              </p>
            </div>
            <div className="flex gap-2">
              {hold?.kind === "loop_budget" ? (
                admin && (
                  <AddLoopsDialog
                    budget={loopBudget}
                    target={{ taskId: task.id }}
                    held
                    trigger={<Button size="sm">Add loops and continue</Button>}
                  />
                )
              ) : (
                <Button size="sm" disabled={resumeTask.isPending} onClick={() => resumeTask.mutate("rework")}>
                  Request rework
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={resumeTask.isPending}
                onClick={() => resumeTask.mutate("continue")}
              >
                Continue anyway
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 @5xl/main:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-6">
          <WorkOrderProduction data={data} access={{ runDetail: true, actions: true }} />
          {task.description && (
            <section aria-label="Description">
              <SectionHeader title="Description" className="mb-1" />
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
                {task.description}
              </p>
            </section>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          {mergeQueue && (
            <Card>
              <CardHeader>
                <CardTitle>Shipping</CardTitle>
                <CardDescription>Merge queue ticket</CardDescription>
              </CardHeader>
              <CardContent>
                <MergeTicket item={mergeQueue} />
              </CardContent>
            </Card>
          )}
          {!detached && (
            <Card>
              <CardHeader>
                <CardTitle>Loop budget</CardTitle>
                <CardDescription>Times the Verifier can send findings back to the Fixer</CardDescription>
              </CardHeader>
              <CardContent>
                <LoopBudgetRow
                  budget={loopBudget}
                  target={{ taskId: task.id }}
                  open={isOpenWorkOrder(task)}
                />
              </CardContent>
            </Card>
          )}
          <ArtifactsCard artifacts={artifacts} />
          {issue && key && (
            <Card>
              <CardHeader>
                <CardTitle>Issue</CardTitle>
                <CardDescription>Snapshot from {task.trackerProvider}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <PropertyList>
                  <PropertyRow label="State">{issue.state || "—"}</PropertyRow>
                  <PropertyRow label="Priority">{issue.priority ?? "—"}</PropertyRow>
                  <PropertyRow label="Assignee">{issue.assignee ?? "—"}</PropertyRow>
                </PropertyList>
                {issue.labels.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {issue.labels.map((l) => (
                      <Badge key={l} variant="secondary">
                        {l}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          {task.instructions && <OperatorInstructions instructions={task.instructions} />}
        </div>
      </div>
    </>
  );
}
